import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Leaf, ShoppingBag, Palette, TrendingUp } from 'lucide-react';

const ReadyToWear = () => {
  const collections = [
    {
      name: "Spring/Summer 2024",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Light, airy pieces perfect for the warmer months."
    },
    {
      name: "Urban Essentials",
      image: "https://images.unsplash.com/photo-1485230895905-ec40ba36b9bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Versatile staples for the modern wardrobe."
    },
    {
      name: "Evening Elegance",
      image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Sophisticated pieces for special occasions."
    },
    {
      name: "Sustainable Luxe",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Eco-conscious fashion that doesn't compromise on style."
    }
  ];

  const features = [
    {
      icon: <Leaf className="h-8 w-8 text-emerald-400" />,
      title: "Sustainable Materials",
      description: "Ethically sourced fabrics and eco-friendly production methods."
    },
    {
      icon: <Sparkles className="h-8 w-8 text-emerald-400" />,
      title: "Quality Craftsmanship",
      description: "Attention to detail in every stitch and finish."
    },
    {
      icon: <Palette className="h-8 w-8 text-emerald-400" />,
      title: "Timeless Design",
      description: "Classic pieces that transcend seasonal trends."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-emerald-400" />,
      title: "Modern Appeal",
      description: "Contemporary styles for today's fashion-forward individual."
    }
  ];

  const categories = [
    {
      name: "Dresses",
      image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      items: ["Midi Dresses", "Maxi Dresses", "Cocktail Dresses", "Day Dresses"]
    },
    {
      name: "Separates",
      image: "https://images.unsplash.com/photo-1485230895905-ec40ba36b9bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      items: ["Blouses", "Skirts", "Trousers", "Jackets"]
    },
    {
      name: "Outerwear",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      items: ["Coats", "Blazers", "Capes", "Trenches"]
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Ready to Wear</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Elevate your everyday style with our contemporary collections
            </p>
          </motion.div>
        </div>
      </section>

      {/* Latest Collections */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Latest Collections</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore our newest ready-to-wear pieces, designed for the modern lifestyle
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {collections.map((collection, index) => (
              <motion.div
                key={collection.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{collection.name}</h3>
                    <p className="text-gray-300">{collection.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Shop by Category</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Find the perfect pieces to complement your wardrobe
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-w-3 aspect-h-4">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors">
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                      <h3 className="text-2xl font-bold mb-4">{category.name}</h3>
                      <ul className="space-y-2">
                        {category.items.map((item, i) => (
                          <li key={i} className="text-gray-300">{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Standards</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Quality and sustainability in every piece
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/10 mb-6">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shopping Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingBag className="h-16 w-16 text-emerald-400 mx-auto mb-8" />
          <h2 className="text-4xl font-bold mb-6">Ready to Shop?</h2>
          <p className="text-gray-400 mb-8">
            Explore our full collection online or visit our boutique for a personalized shopping experience
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-emerald-400 text-black font-semibold rounded-full hover:bg-emerald-300 transition-colors"
            >
              Shop Online
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 border-2 border-emerald-400 text-emerald-400 font-semibold rounded-full hover:bg-emerald-400 hover:text-black transition-colors"
            >
              Book Appointment
            </motion.button>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ReadyToWear; 